// PartnerAnsprechpartnerListe — Abschnitt „Ansprechpartner" in den Details
// eines Produktpartners. Zeigt die Personen, die diesem Partner über ihr
// ContactPartnerProfile zugeordnet sind, und führt per Klick in deren Kontakt.
//
// Bewusst NICHT über ContactRelation: die AP-Bereinigung arbeitet auf den
// Relationen zwischen Kundenfirma und Person und würde Gesellschafts-
// Ansprechpartner sonst als zu bereinigende Altlast einsammeln.

import * as React from "react";
import { useRelStyles } from "../relationsInline/styles";
import { EintragDialog } from "../forms/EintragDialog";
import { ContactRefField } from "../relationsInline/ContactRefField";
import { createContactFromRef } from "../relationsInline/persistRelations";
import type { ContactRef } from "../relationsInline/types";
import { AnsprechpartnerFelder } from "./AnsprechpartnerFelder";
import {
  collectPartnerProfile,
  partnerProfileToFormState,
  PP_FIELDS,
  validatePartnerProfile,
} from "./partnerProfileModel";
import { Link } from "react-router";
import {
  listPartnerAnsprechpartner,
  getPartnerProfile,
  assignPartnerProfile,
  type PartnerAnsprechpartner,
  type PartnerProfile,
} from "../../services/produktpartnerService";
import {
  funktionsbereichLabel,
  positionLabel,
  spartenLabels,
} from "../../data/produktpartnerKatalog";

export type PartnerAnsprechpartnerListeProps = {
  partnerContactId?: number;
  partnerName?: string;
  partnerArt?: "Produktpartner" | "Kooperationspartner";
  nurLesen?: boolean;
};

const anzeigeName = (a: PartnerAnsprechpartner): string => {
  const vorname = (a.callName || a.firstName || "").trim();
  return [a.name, vorname].filter(Boolean).join(", ");
};

/** Zweite Zeile: Position, Abteilung und Standort, soweit gepflegt. */
const beschreibung = (a: PartnerAnsprechpartner): string =>
  [
    positionLabel(a.position),
    a.positionFreitext,
    funktionsbereichLabel(a.funktionsbereich),
    a.organisationseinheit,
    a.standort,
  ]
    .map((v) => (v ?? "").trim())
    .filter(Boolean)
    .join(" · ");

type AnsprechpartnerEntwurf = {
  key: number;
  person: ContactRef;
  profil: Omit<PartnerProfile, "contactId">;
};
export type PartnerAnsprechpartnerListeRef = {
  geaendert: () => boolean;
  speichern: (partnerId: number) => Promise<string[]>;
};

function AnsprechpartnerDialog({
  partnerContactId,
  partnerName,
  partnerArt = "Produktpartner",
  eintrag,
  entwurf,
  onVormerken,
  onSchliessen,
  onGespeichert,
}: PartnerAnsprechpartnerListeProps & {
  eintrag?: PartnerAnsprechpartner;
  entwurf?: AnsprechpartnerEntwurf;
  onVormerken?: (
    person: ContactRef,
    profil: Omit<PartnerProfile, "contactId">,
  ) => void;
  onSchliessen: () => void;
  onGespeichert: () => Promise<void>;
}) {
  const rs = useRelStyles();
  const [person, setPerson] = React.useState<ContactRef | null>(
    entwurf?.person ??
      (eintrag
        ? {
            type: "Person",
            contactId: eintrag.contactId,
            firstName: eintrag.firstName ?? undefined,
            lastName: eintrag.name,
          }
        : null),
  );
  const [felder, setFelder] = React.useState<Record<string, string>>({
    ...partnerProfileToFormState(
      entwurf ? { ...entwurf.profil, contactId: 0 } : null,
    ),
    [PP_FIELDS.partnerContactId]: String(partnerContactId ?? ""),
  });
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [profilLadefehler, setProfilLadefehler] = React.useState(false);
  const [laedt, setLaedt] = React.useState(false);
  const [speichert, setSpeichert] = React.useState(false);
  const gespeichert = React.useRef<number | null>(null);
  const auswahl = React.useRef(0);
  const waehle = async (next: ContactRef | null) => {
    const lauf = ++auswahl.current;
    setPerson(next);
    gespeichert.current = null;
    setFehler(null);
    setProfilLadefehler(false);
    setFelder({
      ...partnerProfileToFormState(null),
      [PP_FIELDS.partnerContactId]: String(partnerContactId ?? ""),
    });
    if (!next?.contactId) {
      setLaedt(false);
      return;
    }
    setLaedt(true);
    try {
      const profil = await getPartnerProfile(next.contactId);
      if (lauf === auswahl.current)
        setFelder({
          ...partnerProfileToFormState(profil),
          [PP_FIELDS.partnerContactId]: String(partnerContactId ?? ""),
        });
    } catch (error) {
      if (lauf === auswahl.current) {
        setProfilLadefehler(true);
        setFehler(
          error instanceof Error
            ? error.message
            : "Profil konnte nicht geladen werden.",
        );
      }
    } finally {
      if (lauf === auswahl.current) setLaedt(false);
    }
  };
  React.useEffect(() => {
    if (!entwurf && person?.contactId) void waehle(person);
    return () => {
      auswahl.current++;
    };
  }, []);
  const f = (key: string) => felder[key] ?? "";
  const speichern = async () => {
    if (speichert || laedt || profilLadefehler) return;
    const pflicht = validatePartnerProfile((key) =>
      key === PP_FIELDS.partnerContactId && !partnerContactId ? "1" : f(key),
    );
    if (
      !person ||
      (!person.contactId &&
        (!person.lastName?.trim() || !person.firstName?.trim()))
    )
      pflicht.unshift(
        "Bitte eine Person auswählen oder Vor- und Nachname angeben.",
      );
    if (pflicht.length) {
      setFehler(pflicht.join(" "));
      return;
    }
    setSpeichert(true);
    setFehler(null);
    try {
      const profil = collectPartnerProfile(f);
      if (onVormerken) {
        onVormerken(person!, profil);
        onSchliessen();
        return;
      }
      if (!partnerContactId)
        throw new Error("Gesellschaft muss zuerst gespeichert werden.");
      const id = gespeichert.current ?? person!.contactId;
      if (id) {
        await assignPartnerProfile(id, profil);
      } else {
        const neu = await createContactFromRef(
          person!,
          {},
          `Ansprechpartner ${partnerArt}`,
          partnerContactId,
          profil,
        );
        if (!neu) throw new Error("Die Person konnte nicht angelegt werden.");
        gespeichert.current = neu;
      }
      await onGespeichert();
      onSchliessen();
    } catch (error) {
      setFehler(
        error instanceof Error
          ? error.message
          : "Speichern fehlgeschlagen. Bitte erneut versuchen.",
      );
    } finally {
      setSpeichert(false);
    }
  };
  return (
    <EintragDialog
      titel={
        eintrag ? "Ansprechpartner bearbeiten" : "Ansprechpartner hinzufügen"
      }
      kicker="Ansprechpartner"
      fehler={fehler}
      beschaeftigt={speichert}
      uebernehmenGesperrt={laedt || profilLadefehler}
      uebernehmenText={onVormerken ? "Übernehmen" : "Speichern"}
      onUebernehmen={() => void speichern()}
      onAbbrechen={() => {
        if (!speichert) onSchliessen();
      }}
    >
      <fieldset
        disabled={speichert || laedt}
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      >
        {eintrag || gespeichert.current ? (
          <p>
            {[person?.lastName, person?.firstName].filter(Boolean).join(", ")}
          </p>
        ) : (
          <ContactRefField
            wantType="Person"
            personOnly
            value={person}
            onChange={(next) => void waehle(next)}
            idBase="partner-ansprechpartner"
          />
        )}
        {profilLadefehler && (
          <button
            type="button"
            className={rs.ghostBtn}
            onClick={() => void waehle(person)}
          >
            Profil erneut laden
          </button>
        )}
        {laedt ? (
          <p>Profil wird geladen …</p>
        ) : (
          <AnsprechpartnerFelder
            f={f}
            set={(key, value) => setFelder((old) => ({ ...old, [key]: value }))}
            partnerArt={partnerArt}
            nurLesen={speichert}
            festeGesellschaft={{
              id: partnerContactId ?? 0,
              name: partnerName ?? `Gesellschaft #${partnerContactId}`,
            }}
          />
        )}
      </fieldset>
    </EintragDialog>
  );
}

export const PartnerAnsprechpartnerListe = React.forwardRef<
  PartnerAnsprechpartnerListeRef,
  PartnerAnsprechpartnerListeProps
>(
  (
    {
      partnerContactId,
      partnerName,
      partnerArt = "Produktpartner",
      nurLesen = false,
    },
    ref,
  ) => {
    const rs = useRelStyles();
    const [rows, setRows] = React.useState<PartnerAnsprechpartner[] | null>(
      null,
    );
    const [fehler, setFehler] = React.useState(false);
    const [entwuerfe, setEntwuerfe] = React.useState<AnsprechpartnerEntwurf[]>(
      [],
    );
    const entwuerfeRef = React.useRef(entwuerfe);
    entwuerfeRef.current = entwuerfe;
    const naechsterKey = React.useRef(0);
    const [entwurfDialog, setEntwurfDialog] =
      React.useState<AnsprechpartnerEntwurf | null>(null);
    const aktualisiereEntwuerfe = (next: AnsprechpartnerEntwurf[]) => {
      entwuerfeRef.current = next;
      setEntwuerfe(next);
    };
    React.useImperativeHandle(ref, () => ({
      geaendert: () => entwuerfeRef.current.length > 0,
      speichern: async (partnerId) => {
        const fehler: string[] = [];
        for (const entwurf of [...entwuerfeRef.current]) {
          try {
            const profil = { ...entwurf.profil, partnerContactId: partnerId };
            if (entwurf.person.contactId)
              await assignPartnerProfile(entwurf.person.contactId, profil);
            else {
              const id = await createContactFromRef(
                entwurf.person,
                {},
                `Ansprechpartner ${partnerArt}`,
                partnerId,
                profil,
              );
              if (!id) throw new Error("Person konnte nicht angelegt werden.");
            }
            aktualisiereEntwuerfe(
              entwuerfeRef.current.filter((e) => e.key !== entwurf.key),
            );
          } catch (error) {
            fehler.push(
              error instanceof Error
                ? error.message
                : "Ansprechpartner konnte nicht gespeichert werden.",
            );
          }
        }
        return fehler;
      },
    }));
    const [dialog, setDialog] = React.useState<
      PartnerAnsprechpartner | "neu" | null
    >(null);
    React.useEffect(() => {
      if (nurLesen) {
        setDialog(null);
        setEntwurfDialog(null);
      }
    }, [nurLesen]);
    React.useEffect(() => {
      if (!partnerContactId) {
        setRows([]);
        return;
      }
      const ctrl = new AbortController();
      setRows(null);
      setFehler(false);
      listPartnerAnsprechpartner(partnerContactId, ctrl.signal)
        .then((rows) => {
          if (!ctrl.signal.aborted) setRows(rows);
        })
        .catch(() => {
          if (!ctrl.signal.aborted) setFehler(true);
        });
      return () => ctrl.abort();
    }, [partnerContactId]);
    return (
      <div className={rs.list}>
        {fehler && (
          <p role="alert">Ansprechpartner konnten nicht geladen werden.</p>
        )}
        {rows === null && !fehler && <p>Ansprechpartner werden geladen …</p>}
        {rows?.length === 0 && entwuerfe.length === 0 && (
          <p>Noch kein Ansprechpartner zugeordnet.</p>
        )}
        {rows?.map((a) => (
          <div key={a.contactId} className={rs.chip}>
            <div className={rs.chipMain}>
              <Link className={rs.chipName} to={`/contacts/${a.contactId}`}>
                {anzeigeName(a)}
              </Link>
              <span className={rs.hint}>{beschreibung(a)}</span>
              {!!a.sparten?.length && (
                <span className={rs.chipType}>
                  {spartenLabels(a.sparten).join(", ")}
                </span>
              )}
            </div>
            <button
              type="button"
              className={rs.chipChange}
              disabled={nurLesen}
              onClick={() => setDialog(a)}
              aria-label={`${anzeigeName(a)} bearbeiten`}
            >
              Bearbeiten
            </button>
          </div>
        ))}
        {entwuerfe.map((e) => (
          <div key={e.key} className={rs.chip}>
            <div className={rs.chipMain}>
              <span className={rs.chipName}>
                {[e.person.lastName, e.person.firstName]
                  .filter(Boolean)
                  .join(", ")}
              </span>
              <span className={rs.hint}>
                {[
                  positionLabel(e.profil.position),
                  funktionsbereichLabel(e.profil.funktionsbereich),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
            <div className={rs.chipActions}>
              <button
                type="button"
                className={rs.chipChange}
                disabled={nurLesen}
                onClick={() => setEntwurfDialog(e)}
              >
                Entwurf bearbeiten
              </button>
              <button
                type="button"
                className={rs.chipChange}
                disabled={nurLesen}
                onClick={() =>
                  aktualisiereEntwuerfe(
                    entwuerfeRef.current.filter((x) => x.key !== e.key),
                  )
                }
              >
                Entwurf entfernen
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className={rs.ghostBtn}
          disabled={nurLesen}
          onClick={() => setDialog("neu")}
        >
          Ansprechpartner hinzufügen
        </button>
        {(dialog || entwurfDialog) && !nurLesen && (
          <AnsprechpartnerDialog
            partnerContactId={partnerContactId}
            partnerName={partnerName}
            partnerArt={partnerArt}
            eintrag={dialog === "neu" || !dialog ? undefined : dialog}
            entwurf={entwurfDialog ?? undefined}
            onVormerken={
              !partnerContactId
                ? (person, profil) => {
                    const next = {
                      key: entwurfDialog?.key ?? ++naechsterKey.current,
                      person,
                      profil,
                    };
                    aktualisiereEntwuerfe([
                      ...entwuerfeRef.current.filter((e) => e.key !== next.key),
                      next,
                    ]);
                  }
                : undefined
            }
            onSchliessen={() => {
              setDialog(null);
              setEntwurfDialog(null);
            }}
            onGespeichert={async () => {
              if (partnerContactId)
                setRows(await listPartnerAnsprechpartner(partnerContactId));
              setFehler(false);
            }}
          />
        )}
      </div>
    );
  },
);
export default PartnerAnsprechpartnerListe;
